/** @jsxImportSource @emotion/react */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.svg';
import { usePlausible } from '../../../hooks/usePlausible';
import { keyframes } from '@emotion/react';

// Define the animation
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

interface PlausibleEvents {
  'High Interest Form View': undefined;
  'High Interest Form Submitted': undefined;
  [key: string]: any;  // Add index signature for other events
}

function HighInterestCandidateApplicationSpanishComponent() {
  const { trackEvent } = usePlausible<PlausibleEvents>();
  const startTime = React.useRef(Date.now());
  const navigate = useNavigate();

  // Track initial form view
  useEffect(() => {
    trackEvent('High Interest Form View', undefined, 0);
  }, [trackEvent]);

  // Handle direct navigation to the thank you page
  const handleContinue = () => {
    try {
      trackEvent('High Interest Form Submitted', undefined, 0);
      navigate('/apply/thank-you');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-[#7B1C14] py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="text-center mb-4">
          <img 
            src={logo} 
            alt="Spicy Cubes" 
            className="h-16 mx-auto"
          />
          <h1 className="text-2xl font-bold text-[#F7E8CC] mt-4">Oportunidad para Creadores: Etapa Final</h1>
        </div>
        
        <div className="bg-[#6E1912] rounded-lg shadow-lg p-6">
          <div className="bg-[#5E150F] rounded-lg p-4 mb-4 border-2 border-[#FFD700] shadow-lg">
            <h2 className="text-xl font-bold text-center text-[#FFD700]">
              ¡Recibe $30 por Tu Video de Prueba!
            </h2>
            <p className="text-center mt-2 text-[#F7E8CC]">
              Recrea uno de estos 3 videos exitosos y envíalo. Recibirás $30 al enviar y serás considerado para contratación.
            </p>
          </div>
          
          <div className="space-y-4 text-[#F7E8CC]/90">
            <p className="font-bold text-center text-[#FFD700]">
              💰 ¡Copia uno de estos videos virales! 💰
            </p>
            
            <div className="space-y-4">
              {/* Example 1 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-1">1. "Es una bandera roja?"</h4>
                <p className="mb-1">
                  <a 
                    href="https://www.tiktok.com/t/ZTjhtDt7P/" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Ver Ejemplo →
                  </a>
                </p>
                <p className="text-sm">
                  Gancho: <span className="font-bold">"Personalmente creo que es una locura pero dime tu..."</span>
                </p>
              </div>
              
              {/* Example 2 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-1">2. "Mi suegra comió nuestros Spicy Cubes"</h4>
                <p className="mb-1">
                  <a 
                    href="https://www.tiktok.com/t/ZTjhtJNGE/" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Ver Ejemplo →
                  </a>
                </p>
                <p className="text-sm">
                  Gancho: <span className="font-bold">"Cuando mi esposo y yo nos casamos las cosas estaban calientes, todo el tiempo..."</span>
                </p>
              </div>
              
              {/* Example 3 */}
              <div className="bg-[#5E150F]/70 p-4 rounded-md">
                <h4 className="font-bold text-[#FFD700] mb-1">3. "El TSA sacó mi 🍆 de mi maleta"</h4>
                <p className="mb-1">
                  <a 
                    href="https://www.tiktok.com/t/ZTjhtUgut/" 
                    className="text-[#FF851B] hover:underline" 
                    target="_blank" 
                    rel="noopener noreferrer">
                    Ver Ejemplo →
                  </a>
                </p>
                <p className="text-sm">
                  Gancho: <span className="font-bold">"Estoy en el aeropuerto y abrieron mi maleta..."</span>
                </p>
              </div>
              
              <div className="p-3 bg-[#5E150F] rounded-md text-center">
                <p className="text-[#FFD700] font-medium">Requisitos:</p>
                <ul className="text-sm mt-1 list-disc pl-5 text-left">
                  <li>El video debe durar al menos 1 minuto</li>
                  <li>El video no debe parecer un anuncio, debe ser una historia</li>
                  <li>Mencionar Spicy Cubes (gomitas afrodisíacas) cerca del final</li>
                </ul>
              </div>
              
              <div className="p-3 bg-[#5E150F] rounded-md text-center">
                <p className="text-[#FFD700] font-medium">Consejos para Tener Éxito:</p>
                <ul className="text-sm mt-1 list-disc pl-5 text-left">
                  <li>Habla como si estuvieras en FaceTime con un amigo</li>
                  <li>Crea suspenso para mantener a los espectadores viendo</li>
                  <li>Graba en un entorno natural (coche, caminando, etc.)</li>
                  <li>Incluye pequeños detalles que generen comentarios</li>
                  <li>Nota: No necesitas tener el producto en el video</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <a 
                href="https://forms.gle/qdC67TSmMFHJTFDc8" 
                target="_blank" 
                rel="noopener noreferrer"
                css={{
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
                className="inline-flex justify-center rounded-md bg-[#FFB700] py-3 px-6 text-lg font-bold text-[#5E150F] shadow-sm hover:bg-[#FFA200] focus:outline-none focus:ring-2 focus:ring-[#FFD700]"
              >
                📹 Envía Tu Video Aquí 📹
              </a>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-[#F7E8CC]/80">
            ¿Preguntas? Contacta a{' '}
            <a 
              href="mailto:ashli@spicycubes.co" 
              className="font-bold text-[#F7E8CC] underline"
            >
              ashli@spicycubes.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export const HighInterestCandidateApplicationSpanish = HighInterestCandidateApplicationSpanishComponent; 